import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, formatDate, useApi } from '../api';

interface Message {
  id: string;
  author: 'customer' | 'staff';
  authorName: string;
  body: string;
  createdAt: string;
}
interface Ticket {
  id: string;
  subject: string;
  category: string;
  status: 'open' | 'answered' | 'closed';
  createdAt: string;
  lastMessageAt: string;
  messages?: Message[];
}

const CATEGORIES = [
  ['connection', 'Connecting to my database'],
  ['performance', 'Slow queries or performance'],
  ['data', 'Data, backups or restore'],
  ['billing', 'Billing and credit'],
  ['account', 'Account and team'],
  ['other', 'Something else'],
] as const;

function StatusPill({ status }: { status: Ticket['status'] }) {
  const cls = status === 'answered' ? 'active' : status === 'closed' ? '' : 'provisioning';
  return <span className={`badge ${cls}`}>{status === 'answered' ? 'answered' : status}</span>;
}

/** Customer support: open a ticket, read the thread, reply. Everything also goes out by email. */
export function SupportPage() {
  const tickets = useApi<{ tickets: Ticket[] }>('/support/tickets');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<string>('connection');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  return (
    <div>
      <h1>Support</h1>
      <div className="grid cols-2">
        <div className="card">
          <h2>Ask us anything</h2>
          <p className="muted">
            A person answers, usually within a few hours on weekdays. You will get a reply here and by email.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setBusy(true);
              setError(null);
              api<{ ticket: Ticket }>('/support/tickets', {
                method: 'POST',
                json: { subject, category, body },
              })
                .then((r) => {
                  setSent(r.ticket.id);
                  setSubject('');
                  setBody('');
                  return tickets.reload();
                })
                .catch((err: Error) => setError(err.message))
                .finally(() => setBusy(false));
            }}
          >
            <div className="field">
              <label>Topic</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                maxLength={120}
                placeholder="Short summary"
              />
            </div>
            <div className="field">
              <label>Message</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                minLength={10}
                maxLength={8000}
                rows={7}
                placeholder="What happened, what you expected, and anything you already tried. Never paste passwords."
              />
            </div>
            {error && <p className="error">{error}</p>}
            {sent && (
              <p className="ok">
                Sent. <Link to={`/support/${sent}`}>View the ticket</Link>.
              </p>
            )}
            <button type="submit" disabled={busy}>
              Send
            </button>
          </form>
          <p className="muted" style={{ marginTop: 12 }}>
            Prefer email? Write to <a href="mailto:support@tallypg.com">support@tallypg.com</a>.
          </p>
        </div>
        <div className="card">
          <h2>Your tickets</h2>
          {tickets.data?.tickets.length === 0 && <p className="muted">No tickets yet.</p>}
          <table>
            <tbody>
              {tickets.data?.tickets.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/support/${t.id}`}>{t.subject}</Link>
                    <div className="muted">{t.category}</div>
                  </td>
                  <td>
                    <StatusPill status={t.status} />
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(t.lastMessageAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function TicketPage() {
  const { ticketId } = useParams();
  const ticket = useApi<{ ticket: Ticket }>(`/support/tickets/${ticketId}`);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (ticket.error) return <p className="error">{ticket.error.message}</p>;
  const t = ticket.data?.ticket;
  if (!t) return <p className="muted">Loading…</p>;
  return (
    <div>
      <p className="muted">
        <Link to="/support">Support</Link> / {t.subject}
      </p>
      <h1>
        {t.subject} <StatusPill status={t.status} />
      </h1>
      <div className="card">
        {t.messages?.map((m) => (
          <div key={m.id} className={`msg ${m.author}`}>
            <div className="muted" style={{ fontSize: 12 }}>
              <strong>{m.author === 'staff' ? `${m.authorName} · TallyPG` : m.authorName}</strong> ·{' '}
              {formatDate(m.createdAt)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <h2>{t.status === 'closed' ? 'Reopen with a reply' : 'Reply'}</h2>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={8000} />
        {error && <p className="error">{error}</p>}
        <div className="row" style={{ marginTop: 10 }}>
          <button
            disabled={busy || body.trim().length < 2}
            onClick={() => {
              setBusy(true);
              setError(null);
              api(`/support/tickets/${t.id}/messages`, { method: 'POST', json: { body } })
                .then(() => {
                  setBody('');
                  return ticket.reload();
                })
                .catch((err: Error) => setError(err.message))
                .finally(() => setBusy(false));
            }}
          >
            Send reply
          </button>
        </div>
      </div>
    </div>
  );
}
